'use client';

import Image from 'next/image';
import React, { forwardRef, useContext } from 'react';
import { ArrowRight, CheckCircle, Sparkles } from 'lucide-react';
import { P } from '@/components/ui/P';
import { Button } from '@/components/ui/button';
import { PopupContext } from '@/context/PopupContext';
import FormOrder from '@/components/form-order';
import { CardServiceProps } from '@/data/sale-data';

interface ServiceProps {
    cardsService: CardServiceProps[];
}

const Service = forwardRef<HTMLDivElement, ServiceProps>(({ cardsService }, ref) => {
    const { openPopup } = useContext(PopupContext);

    const handleClick = (card: CardServiceProps) => {
        if (!card.action) return;
        if (card.action === 'scroll' && card.onCardClick) {
            card.onCardClick();
        }
        openPopup(card.action);
    };

    return (
        <section ref={ref} className="mt-6 mx-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {cardsService.map((card, index) => (
                    <article
                        key={index}
                        className="relative flex flex-col gap-5 rounded-3xl bg-white/80 p-6 border border-gray-100 shadow-md hover:shadow-lg hover:-translate-y-1 transition-transform duration-300"
                    >
                        {/* badge */}
                        <span className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 shadow-sm">
              <Sparkles size={14} />
              Услуга
            </span>

                        {/* icon / image */}
                        <div className="mb-3">
                            {typeof card.path === 'string' ? (
                                <Image
                                    src={card.path}
                                    alt={card.title}
                                    width={48}
                                    height={48}
                                    className="h-12 w-12 object-contain"
                                />
                            ) : (
                                card.path
                            )}
                        </div>

                        {/* title */}
                        <P size="m" className="font-bold text-gray-900 leading-snug">
                            {card.title}
                        </P>

                        {/* description */}
                        <P size="s" className="text-gray-600">
                            {card.description}
                        </P>

                        {/* divider */}
                        <div className="h-px bg-gray-200"></div>

                        {/* tags */}
                        {card.tag && (
                            <div className="flex flex-col gap-3">
                                <P size="s" className="text-gray-800 font-semibold">
                                    Что входит:
                                </P>
                                <ul className="space-y-2">
                                    {card.tag.map((t, tagIndex) => (
                                        <li key={tagIndex} className="flex items-start gap-2 text-sm text-gray-700">
                                            <CheckCircle size={16} className="text-green-500 mt-[2px]" />
                                            {t.text}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* desktop CTA */}
                        <Button
                            variant="outline"
                            size="sm"
                            className="mt-4 gap-2 hidden lg:inline-flex"
                            onClick={() => handleClick(card)}
                        >
                            Подробнее
                            <ArrowRight size={16} />
                        </Button>

                        {/* mobile inline form */}
                        <div className="lg:hidden mt-4">
                            <P size="s" className="text-gray-800 font-medium">
                                Оставьте заявку
                            </P>
                            <FormOrder className="mt-2" />
                        </div>

                        {/* index marker */}
                    </article>
                ))}
            </div>
        </section>
    );
});

Service.displayName = 'Service';
export default Service;
